import { Project, GetProjectsByFilterProps, Nullable } from '@brg/api';
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchProjectsGrid,
  fetchProjectsGridMore,
} from '../actions/projectsGridActions';
import { merge } from 'shared/helpers/utils/filters';
import { initialRatingsFilter, IShowcaseRatingsFilter } from 'shared/constants/ShowcaseRatings';

const INITIAL_COUNT = 0;


interface ITabs {
  [key: string]: Project<'Local' | 'Global'>[]
}

interface IPagingList {
  [key: string]: {
    offset: number;
    limit: number;
    count: number;
    pages: number;
    page: number;
  }[]
}

interface ProjectsGridsState extends GetProjectsByFilterProps {
  prevTeam: string;
  data: any;
  tabs: any;
  fetchingTabs: boolean;
  fetchingMore: boolean;
  context?: string;
  tabsLoading?: any;
  ratingsFilter?: IShowcaseRatingsFilter;
  prevProductCardFilter?: string;
}


const makeTabs = ({ categories, tabs, mainFilter, mainEntityType, separateFilter }: any) => {
  const mergedCategories = categories?.map((category: any) => {
    return {
      ...category,
      filter: merge(category.filter, mainFilter),
      entityType: category.entityType || mainEntityType,
    }
  })

  if (tabs?.length) {
    const filledCategories = tabs?.reduce((res: any, tab: any) => {

      const { category } = tab;

      if (separateFilter) {
        return {
          ...res,
          main: [...(res?.main || []), {
            ...tab,
            filter: tab.filter,
            entityType: tab.entityType || mainEntityType,
          }]
        }
      }
      if (mergedCategories?.length && category) {
        return {
          ...res,
          [category]: [...(res?.[category] || []), tab]
        }
      }
      
      return {
        ...res,
        main: [...(res?.main || []), {
          ...tab,
          filter: merge(tab.filter, mainFilter),
          entityType: tab.entityType || mainEntityType,
        }]
      }
    }, {})

    if (mergedCategories?.length) {
      mergedCategories.forEach(({ path, entityType: categoryEntityType, filter: categoryFilter }: any) => {
        if (!filledCategories[path]) {
          filledCategories[path] = [{
            path: 'all',
            entityType: categoryEntityType,
            name: [],
            filter: categoryFilter,
          }]
        } else {
          filledCategories[path] = filledCategories[path].map((tab: any) => {
            const { entityType: tabEntityType, filter: tabFilter } = tab;
            return {
              ...tab,
              filter: merge(categoryFilter, tabFilter),
              entityType: tabEntityType || categoryEntityType,
            }
          })
        }
      })
    }

    return filledCategories;
  }
  if (categories?.length) {
    return mergedCategories.reduce((res: any, { path, entityType, filter }: any) => {
      return {
        ...res,
        [path]: [{
          path: 'all',
          name: [],
          filter,
          entityType,
        }]
      }
    }, {})
  }
  return {
    main: [{
      path: 'all',
      name: [],
      filter: mainFilter,
      entityType: mainEntityType,
    }]
  }
}

const initialState = {
  prevTeam: null,
  tabs: {},
  data: {},
  fetchingTabs: false,
  fetchingMore: false,
  context: null,
  tabsLoading: {},
  currentTeam: null,
  count: INITIAL_COUNT,
  ratingsFilter: initialRatingsFilter,
  prevProductCardFilter: false
}

export const ProjectsGridSlice = createSlice({
  name: 'projectsGrid',
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    resetProjects: (state) => {
      state.data = {}
    },
    setTabs: (state, action) => {
      state.tabs = makeTabs(action.payload);
      state.context = action.payload.context;
    },
    resetTabs: (state) => {
      state.tabs = initialState.tabs;
    },
    setPrevTeam: (state, action) => {
      state.prevTeam = action.payload;
    },
    setPrevProductCardsFilter: (state, action) => {
      state.prevProductCardFilter = action.payload
    },
    setCurrentTeam: (state, action) => {
      state.currentTeam = action.payload;
    },
    setSingleTabData: (state, action) => {
      const {
        currentCategory,
        currentTab,
        data,
        paging,
        isFinalPage,
        entityType,
        isDrafts,
        contextName,
        hasAnnotation,
        usingFilterOrManual,
        hash,
      } = action.payload

      if (contextName === state.currentTeam) {
        state.data = {
          ...state.data,
          [currentCategory]: {
            //@ts-ignore
            ...(state?.data?.[currentCategory] || {}),
            [currentTab]: {
              paging,
              loaded: true,
              isFinalPage,
              entityType,
              isDrafts,
              hasAnnotation,
              usingFilterOrManual,
              hash,
              context: contextName,
              //@ts-ignore
              data,
            }
          }
        }

        state.tabsLoading = {
          //@ts-ignore
          [currentCategory]: {
            //@ts-ignore
            ...(state?.tabsLoading?.[currentCategory] || {}),
            [currentTab]: false,
          }
        }
        state.count = paging?.count
      }
    },
    setTabsLoading: (state, action) => {
      const { currentCategory, value } = action.payload;
      state.tabsLoading = {
        //@ts-ignore
        [currentCategory]: state.tabs[currentCategory].reduce((acc, { path }) => ({
          ...acc,
          [path]: value,
        }), {})
      }
    },
    setTabLoading: (state, action) => {
      const { currentCategory, currentTab, value } = action.payload;
      state.tabsLoading = {
        //@ts-ignore
        [currentCategory]: {
          ...state.tabsLoading[currentCategory],
          [currentTab]: value,
        }
      }
    },
    setRatingsFilter: (state, action) => {
      const key = action.payload.field;
      const value = action.payload.value;
      state.ratingsFilter = {
        ...(state?.ratingsFilter ?? {}),
        [key]: value,
      }
    },
    patchRatingsFilter: (state, action) => {
      state.ratingsFilter = {
        ...(state?.ratingsFilter ?? {}),
        ...action.payload,
      }
    }
  },
  extraReducers: {
    [fetchProjectsGrid.fulfilled.toString()]: (state, action) => {
      const { contextName, currentCategory, data } = action.payload;
      if (contextName === state.currentTeam) {
        if (contextName === state.context) {
          state.data = {
            ...state.data,
            [currentCategory]: {
              //@ts-ignore
              ...(state?.data?.[currentCategory] || {}),
              ...data,
            }
          }
        } else {
          state.data = {
            [currentCategory]: data,
          }
        }
        state.tabsLoading = {};
        state.fetchingTabs = false;
      }
    },
    [fetchProjectsGrid.rejected.toString()]: (state, action) => {
      if (action?.error?.name !== 'AbortError') {
        state.tabsLoading = {};
        state.fetchingTabs = false;
      }
    },
    [fetchProjectsGrid.pending.toString()]: (state) => {
      state.fetchingTabs = true;
    },

    [fetchProjectsGridMore.fulfilled.toString()]: (state, action) => {
      const { currentCategory, currentTab, data, paging, isFinalPage, entityType, usingFilterOrManual } = action.payload
      state.fetchingMore = false;
      state.data = {
        ...state.data,
        [currentCategory]: {
          //@ts-ignore
          ...state.data[currentCategory],
          [currentTab]: {
            //@ts-ignore
            ...state.data[currentCategory][currentTab],
            paging,
            isFinalPage,
            entityType,
            usingFilterOrManual,
            //@ts-ignore
            data: [...state.data[currentCategory][currentTab].data, ...data]
          }
        }
      }
    },
    [fetchProjectsGridMore.rejected.toString()]: (state) => {
      state.fetchingMore = false;
    },
    [fetchProjectsGridMore.pending.toString()]: (state) => {
      state.fetchingMore = true;
    },
  }
});

export default ProjectsGridSlice;
export const ProjectsGridActions = ProjectsGridSlice.actions
export const ProjectsGridSelector = (store: { projectsGrid: ProjectsGridsState }): ProjectsGridsState => store.projectsGrid;
import { Project, GetProjectsByFilterProps, Nullable } from '@brg/api';
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchProjectsGrid,
  fetchProjectsGridMore,
} from '../actions/projectsGridActions';
import { merge } from 'shared/helpers/utils/filters';
import { initialRatingsFilter, IShowcaseRatingsFilter } from 'shared/constants/ShowcaseRatings';

const INITIAL_COUNT = 0;


interface ITabs {
  [key: string]: Project<'Local' | 'Global'>[]
}

interface IPagingList {
  [key: string]: {
    offset: number;
    limit: number;
    count: number;
    pages: number;
    page: number;
  }[]
}

interface ProjectsGridsState extends GetProjectsByFilterProps {
  prevTeam: string;
  data: any;
  tabs: any;
  fetchingTabs: boolean;
  fetchingMore: boolean;
  context?: string;
  tabsLoading?: any;
  ratingsFilter?: IShowcaseRatingsFilter;
  prevProductCardFilter?: string;
}


const makeTabs = ({ categories, tabs, mainFilter, mainEntityType, separateFilter }: any) => {
  const mergedCategories = categories?.map((category: any) => {
    return {
      ...category,
      filter: merge(category.filter, mainFilter),
      entityType: category.entityType || mainEntityType,
    }
  })

  if (tabs?.length) {
    const filledCategories = tabs?.reduce((res: any, tab: any) => {

      const { category } = tab;

      if (separateFilter) {
        return {
          ...res,
          main: [...(res?.main || []), {
            ...tab,
            filter: tab.filter,
            entityType: tab.entityType || mainEntityType,
          }]
        }
      }
      if (mergedCategories?.length && category) {
        return {
          ...res,
          [category]: [...(res?.[category] || []), tab]
        }
      }
      
      return {
        ...res,
        main: [...(res?.main || []), {
          ...tab,
          filter: merge(tab.filter, mainFilter),
          entityType: tab.entityType || mainEntityType,
        }]
      }
    }, {})

    if (mergedCategories?.length) {
      mergedCategories.forEach(({ path, entityType: categoryEntityType, filter: categoryFilter }: any) => {
        if (!filledCategories[path]) {
          filledCategories[path] = [{
            path: 'all',
            entityType: categoryEntityType,
            name: [],
            filter: categoryFilter,
          }]
        } else {
          filledCategories[path] = filledCategories[path].map((tab: any) => {
            const { entityType: tabEntityType, filter: tabFilter } = tab;
            return {
              ...tab,
              filter: merge(categoryFilter, tabFilter),
              entityType: tabEntityType || categoryEntityType,
            }
          })
        }
      })
    }

    return filledCategories;
  }
  if (categories?.length) {
    return mergedCategories.reduce((res: any, { path, entityType, filter }: any) => {
      return {
        ...res,
        [path]: [{
          path: 'all',
          name: [],
          filter,
          entityType,
        }]
      }
    }, {})
  }
  return {
    main: [{
      path: 'all',
      name: [],
      filter: mainFilter,
      entityType: mainEntityType,
    }]
  }
}

const initialState = {
  prevTeam: null,
  tabs: {},
  data: {},
  fetchingTabs: false,
  fetchingMore: false,
  context: null,
  tabsLoading: {},
  currentTeam: null,
  count: INITIAL_COUNT,
  ratingsFilter: initialRatingsFilter,
  prevProductCardFilter: false
}

export const ProjectsGridSlice = createSlice({
  name: 'projectsGrid',
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    resetProjects: (state) => {
      state.data = {}
    },
    setTabs: (state, action) => {
      state.tabs = makeTabs(action.payload);
      state.context = action.payload.context;
    },
    resetTabs: (state) => {
      state.tabs = initialState.tabs;
    },
    setPrevTeam: (state, action) => {
      state.prevTeam = action.payload;
    },
    setPrevProductCardsFilter: (state, action) => {
      state.prevProductCardFilter = action.payload
    },
    setCurrentTeam: (state, action) => {
      state.currentTeam = action.payload;
    },
    setSingleTabData: (state, action) => {
      const {
        currentCategory,
        currentTab,
        data,
        paging,
        isFinalPage,
        entityType,
        isDrafts,
        contextName,
        hasAnnotation,
        usingFilterOrManual,
        hash,
      } = action.payload

      if (contextName === state.currentTeam) {
        state.data = {
          ...state.data,
          [currentCategory]: {
            //@ts-ignore
            ...(state?.data?.[currentCategory] || {}),
            [currentTab]: {
              paging,
              loaded: true,
              isFinalPage,
              entityType,
              isDrafts,
              hasAnnotation,
              usingFilterOrManual,
              hash,
              context: contextName,
              //@ts-ignore
              data,
            }
          }
        }

        state.tabsLoading = {
          //@ts-ignore
          [currentCategory]: {
            //@ts-ignore
            ...(state?.tabsLoading?.[currentCategory] || {}),
            [currentTab]: false,
          }
        }
        state.count = paging?.count
      }
    },
    setTabsLoading: (state, action) => {
      const { currentCategory, value } = action.payload;
      state.tabsLoading = {
        //@ts-ignore
        [currentCategory]: state.tabs[currentCategory].reduce((acc, { path }) => ({
          ...acc,
          [path]: value,
        }), {})
      }
    },
    setTabLoading: (state, action) => {
      const { currentCategory, currentTab, value } = action.payload;
      state.tabsLoading = {
        //@ts-ignore
        [currentCategory]: {
          ...state.tabsLoading[currentCategory],
          [currentTab]: value,
        }
      }
    },
    setRatingsFilter: (state, action) => {
      const key = action.payload.field;
      const value = action.payload.value;
      state.ratingsFilter = {
        ...(state?.ratingsFilter ?? {}),
        [key]: value,
      }
    },
    patchRatingsFilter: (state, action) => {
      state.ratingsFilter = {
        ...(state?.ratingsFilter ?? {}),
        ...action.payload,
      }
    }
  },
  extraReducers: {
    [fetchProjectsGrid.fulfilled.toString()]: (state, action) => {
      const { contextName, currentCategory, data } = action.payload;
      if (contextName === state.currentTeam) {
        if (contextName === state.context) {
          state.data = {
            ...state.data,
            [currentCategory]: {
              //@ts-ignore
              ...(state?.data?.[currentCategory] || {}),
              ...data,
            }
          }
        } else {
          state.data = {
            [currentCategory]: data,
          }
        }
        state.tabsLoading = {};
        state.fetchingTabs = false;
      }
    },
    [fetchProjectsGrid.rejected.toString()]: (state, action) => {
      if (action?.error?.name !== 'AbortError') {
        state.tabsLoading = {};
        state.fetchingTabs = false;
      }
    },
    [fetchProjectsGrid.pending.toString()]: (state) => {
      state.fetchingTabs = true;
    },

    [fetchProjectsGridMore.fulfilled.toString()]: (state, action) => {
      const { currentCategory, currentTab, data, paging, isFinalPage, entityType, usingFilterOrManual } = action.payload
      state.fetchingMore = false;
      state.data = {
        ...state.data,
        [currentCategory]: {
          //@ts-ignore
          ...state.data[currentCategory],
          [currentTab]: {
            //@ts-ignore
            ...state.data[currentCategory][currentTab],
            paging,
            isFinalPage,
            entityType,
            usingFilterOrManual,
            //@ts-ignore
            data: [...state.data[currentCategory][currentTab].data, ...data]
          }
        }
      }
    },
    [fetchProjectsGridMore.rejected.toString()]: (state) => {
      state.fetchingMore = false;
    },
    [fetchProjectsGridMore.pending.toString()]: (state) => {
      state.fetchingMore = true;
    },
  }
});

export default ProjectsGridSlice;
export const ProjectsGridActions = ProjectsGridSlice.actions
export const ProjectsGridSelector = (store: { projectsGrid: ProjectsGridsState }): ProjectsGridsState => store.projectsGrid;
